import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CategoryConfigurationDefinitionResponse,
  FieldConfigurationRequest,
  SkuType,
} from '@ydistri/api-sdk';
import { styled } from 'styled-components';
import { computeRemSize } from '@ydistri/theme';
import { ConfigurationInput } from '@ydistri/ds';
import { ConfigurationFieldStatus } from '@ydistri/utils';
import useCategoryId from '../../../../../hooks/useCategoryId';
import { useCategoryConfiguration } from '../../../../../hooks/useCategoryConfiguration';
import { usePatchCategoryConfigurationMutation } from '../../../apiTemplate';
import { useTemplateOrCalculation } from '../../../../../hooks/useTemplateOrCalculation';
import { defaultHfmqPairs, HFMQ_PAIR_COUNT, HfmqPair, parseHfmqPairs } from './hfmqPairsLib';
import useIsConfigurationEditable from '../../../../../hooks/useIsConfigurationEditable';
import { isRootCategory } from '../../../../../lib/categories/categoriesLib';

const HfmqPairsGrid = styled.div`
  display: grid;
  grid-template-columns: auto repeat(${HFMQ_PAIR_COUNT}, 1fr);
  grid-template-rows: auto repeat(2, auto);
  align-items: center;
  gap: 4px;
  padding: 4px;
`;

const GridRowLabel = styled.div`
  font-size: ${computeRemSize(12)};
  white-space: nowrap;
  color: #555;
`;

interface ConfigurationHfmqPairsProps {
  definition: CategoryConfigurationDefinitionResponse;
  forcedCategoryId?: number;
}

/**
 * Renders an editable grid of HFMQ (High-Frequency Minimum Quantity) pairs, for the root category as editable
 * Each pair consists of a "price up to" threshold and a "min percentage" value.
 * The last column's price field is read-only and shows an open-ended "above X" label.
 *
 * Save behavior — abort/replace:
 * All pairs are serialised into a single JSON value and sent as one PATCH request.
 * Every time the user leaves a field, any in-flight request is aborted and a new one
 * is fired immediately with the latest snapshot of all pairs. This eliminates race
 * conditions that would otherwise arise from overlapping requests carrying different
 * snapshots of the pair data.
 *
 * Only fields that have unsaved changes show a loading spinner while the save is
 * in progress; the rest remain interactive.
 */
const ConfigurationHfmqPairs: React.FC<ConfigurationHfmqPairsProps> = ({
  definition,
  forcedCategoryId,
}) => {
  const isConfigurationEditable = useIsConfigurationEditable();
  const templateOrCalculation = useTemplateOrCalculation();
  const selectedCategoryId = useCategoryId();
  const categoryId = forcedCategoryId ?? selectedCategoryId;

  const { categoryConfigurationData: configurations } = useCategoryConfiguration(categoryId);

  // Raw JSON string stored in the DB for the FastMover SKU type
  // eslint-disable-next-line typescript/no-unnecessary-condition -- configurations may be undefined for manual calculations
  const storedValue = configurations?.[definition.fieldType]?.[SkuType.FastMover]?.value;

  // Server-side truth: parse once whenever the stored value changes
  const parsedPairs = useMemo(() => parseHfmqPairs(storedValue), [storedValue]);

  // Displayed in the UI — updated on every keystroke via onChange handlers
  const [pairs, setPairs] = useState<HfmqPair[]>(parsedPairs);

  // Latest committed pair snapshot, read imperatively inside the debounce callback.
  // A plain ref is used here because reading pairs state inside a setTimeout closure
  // would capture a stale value from the render that created the closure.
  const dirtyPairsRef = useRef<HfmqPair[]>(parsedPairs);

  // True whenever the UI contains edits that have not yet been sent to the server.
  // A ref (not state) so it can be checked and updated synchronously inside callbacks
  // without triggering re-renders.
  const isDirtyRef = useRef(false);

  const [patchConfiguration] = usePatchCategoryConfigurationMutation();

  // Set of field keys whose values have changed since the last successful save.
  // Used to show a per-field loading spinner — only the fields that are being saved
  // get the LOADING status; untouched fields stay DEFAULT.
  const [dirtyFieldKeys, setDirtyFieldKeys] = useState<Set<string>>(new Set());

  // True while a PATCH request is in-flight. Combined with dirtyFieldKeys to decide
  // which fields show the loading indicator.
  const [isSaving, setIsSaving] = useState(false);

  // Handle to the in-flight RTK Query request so we can abort it when a newer
  // blur arrives before the previous request completes (abort/replace pattern).
  const pendingRequestRef = useRef<ReturnType<typeof patchConfiguration> | null>(null);

  // Sync server data into local state, but only when there are no pending local edits.
  // Without this guard a slow server response arriving while the user is editing another
  // field would silently overwrite their uncommitted changes.
  useEffect(() => {
    if (!isDirtyRef.current) {
      setPairs(parsedPairs);
      dirtyPairsRef.current = parsedPairs;
    }
  }, [parsedPairs]);

  // Sends the current pair snapshot to the server as a single PATCH request.
  // Returns the RTK Query request object so the caller can attach .then() handlers
  // and call .abort() if needed.
  const save = useCallback(
    (updatedPairs: HfmqPair[]) => {
      const payload: FieldConfigurationRequest[] = [
        {
          configurationFieldType: definition.fieldType,
          value: JSON.stringify(updatedPairs),
          skuType: undefined,
        },
      ];

      return patchConfiguration({
        templateId: templateOrCalculation.id,
        categoryId,
        payload,
      });
    },
    [categoryId, definition.fieldType, patchConfiguration, templateOrCalculation.id],
  );

  // When the user navigates to a different category, discard any pending work for
  // the previous category so it doesn't bleed into the new one.
  useEffect(() => {
    if (pendingRequestRef.current) {
      pendingRequestRef.current.abort();
      pendingRequestRef.current = null;
    }
    isDirtyRef.current = false;
    setIsSaving(false);
    setDirtyFieldKeys(new Set());
  }, [categoryId]);

  // Flush unsaved edits when the component is unmounted (e.g. the user closes the
  // panel while a save is still pending).
  useEffect(() => {
    return () => {
      if (isDirtyRef.current) {
        save(dirtyPairsRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- save on unmount only
  }, []);

  // Updates local state when the user changes a "price up to" value.
  // Both pairs state (for rendering) and dirtyPairsRef (for the next save) are kept
  // in sync. The field key is added to dirtyFieldKeys so it will show a spinner
  // when the save fires.
  const handlePriceUpToChange = useCallback(
    (index: number, fieldKey: string, value: number | string) => {
      const numericValue = typeof value === 'string' ? parseFloat(value) : value;
      const updated = pairs.map((pair, i) =>
        i === index ? { ...pair, priceUpTo: numericValue } : pair,
      );
      setPairs(updated);
      dirtyPairsRef.current = updated;
      isDirtyRef.current = true;
      setDirtyFieldKeys(prev => new Set([...prev, fieldKey]));
    },
    [pairs],
  );

  // Same as handlePriceUpToChange but for the "min percentage" row.
  const handleMinPercentageChange = useCallback(
    (index: number, fieldKey: string, value: number | string) => {
      const numericValue = typeof value === 'string' ? parseInt(value) : value;
      const updated = pairs.map((pair, i) =>
        i === index ? { ...pair, minPercentage: numericValue } : pair,
      );
      setPairs(updated);
      dirtyPairsRef.current = updated;
      isDirtyRef.current = true;
      setDirtyFieldKeys(prev => new Set([...prev, fieldKey]));
    },
    [pairs],
  );

  // Called when focus leaves any individual field.
  // Implements the abort/replace pattern:
  //   1. Abort the in-flight request (if any) — its snapshot is stale; a new one
  //      carrying all changes so far will replace it immediately.
  //   2. Fire a new PATCH with the latest pair snapshot.
  //
  // The stale-callback guard inside .then() / .catch() (pendingRequestRef !== request)
  // ensures that a late-arriving response from an aborted request cannot clear
  // isSaving or dirtyFieldKeys — only the most recent request is allowed to do that.
  const handleFieldBlur = useCallback(() => {
    // Nothing changed since the last save — no need to do anything
    if (!isDirtyRef.current) return;

    // Abort any in-flight save — a newer snapshot will replace it immediately
    if (pendingRequestRef.current) {
      pendingRequestRef.current.abort();
      pendingRequestRef.current = null;
    }

    // Mark clean before the async request so that a parsedPairs update arriving
    // during the in-flight request is allowed to sync into local state
    isDirtyRef.current = false;
    setIsSaving(true);

    const request = save(dirtyPairsRef.current);
    pendingRequestRef.current = request;

    request.then(
      () => {
        // A newer blur may have aborted this request and launched a replacement.
        // In that case pendingRequestRef no longer points to us — bail out.
        if (pendingRequestRef.current !== request) return;
        pendingRequestRef.current = null;
        setIsSaving(false);
        setDirtyFieldKeys(new Set());
      },
      () => {
        if (pendingRequestRef.current !== request) return;
        pendingRequestRef.current = null;
        setIsSaving(false);
        // Intentionally keep dirtyFieldKeys populated so the user can see
        // which fields still have unsaved changes after the error
      },
    );
  }, [save]);

  const disabled = !(isConfigurationEditable && isRootCategory(categoryId));
  const currentPairs = pairs.length > 0 ? pairs : defaultHfmqPairs();

  return (
    <HfmqPairsGrid>
      {/* Labels are placed with explicit grid coordinates so that the DOM order of the
          data cells can be column-first (col0-price, col0-pct, col1-price, col1-pct …)
          while the visual layout remains row-first (all prices on row 1, all percentages
          on row 2). Tab order follows DOM order, which gives the desired column-first
          keyboard navigation. */}
      <GridRowLabel style={{ gridRow: 1, gridColumn: 1 }}>Price up to</GridRowLabel>
      <GridRowLabel style={{ gridRow: 2, gridColumn: 1 }}>Percentage</GridRowLabel>

      {currentPairs.map((pair, i) => {
        const pairKey = `Field-Pairs-${i}`;
        const priceKey = `Field-PriceUpTo-${i}`;
        const pctKey = `Field-Percentage-${i}`;
        // The last "price up to" cell is read-only — it represents the open-ended
        // upper bound ("above X") and is not user-editable
        const isLastColumn = i === currentPairs.length - 1;

        return (
          <React.Fragment key={pairKey}>
            {/* Price up to cell — last column shows a read-only placeholder */}
            {isLastColumn ? (
              <div style={{ gridRow: 1, gridColumn: i + 2 }}>
                <ConfigurationInput
                  disabled={true}
                  placeholder={`Above ${currentPairs[i - 1].priceUpTo}`}
                  status={ConfigurationFieldStatus.DEFAULT}
                  value={0}
                  width={computeRemSize(90)}
                />
              </div>
            ) : (
              // The onBlur wrapper div is the save trigger (abort/replace).
              // ConfigurationInput's onChange fires on blur/Enter (not each keystroke),
              // so by the time the div's onBlur bubbles up the ref already holds the
              // committed value.
              <div style={{ gridRow: 1, gridColumn: i + 2 }} onBlur={handleFieldBlur}>
                <ConfigurationInput
                  status={
                    isSaving && dirtyFieldKeys.has(priceKey)
                      ? ConfigurationFieldStatus.LOADING
                      : ConfigurationFieldStatus.DEFAULT
                  }
                  overwritten={false}
                  parentException={false}
                  value={pair.priceUpTo}
                  disabled={disabled}
                  width={computeRemSize(60)}
                  onChange={value => handlePriceUpToChange(i, priceKey, value)}
                />
              </div>
            )}

            {/* Percentage cell — always editable (no last-column special case) */}
            <div style={{ gridRow: 2, gridColumn: i + 2 }} onBlur={handleFieldBlur}>
              <ConfigurationInput
                status={
                  isSaving && dirtyFieldKeys.has(pctKey)
                    ? ConfigurationFieldStatus.LOADING
                    : ConfigurationFieldStatus.DEFAULT
                }
                overwritten={false}
                parentException={false}
                value={pair.minPercentage}
                disabled={disabled}
                width={computeRemSize(60)}
                onChange={value => handleMinPercentageChange(i, pctKey, value)}
              />
            </div>
          </React.Fragment>
        );
      })}
    </HfmqPairsGrid>
  );
};

export default ConfigurationHfmqPairs;
